import Dexie, { type Table } from 'dexie';
import type {
  ArticleRow,
  ChainRow,
  ListItemRow,
  ListRow,
  MetaEntry,
  OrderEventRow,
  OutboxEntry,
  ProductLinkRow,
  ProductRow,
  PromotionRow,
  ReceiptLineRow,
  ReceiptRow,
  StoreRow,
  VisitRow,
} from './types';

export class OdhaczkaDb extends Dexie {
  chains!: Table<ChainRow, string>;
  stores!: Table<StoreRow, string>;
  lists!: Table<ListRow, string>;
  listItems!: Table<ListItemRow, string>;
  products!: Table<ProductRow, string>;
  productLinks!: Table<ProductLinkRow, string>;
  visits!: Table<VisitRow, string>;
  articles!: Table<ArticleRow, string>;
  receipts!: Table<ReceiptRow, string>;
  receiptLines!: Table<ReceiptLineRow, string>;
  promotions!: Table<PromotionRow, string>;
  orderEvents!: Table<OrderEventRow, string>;
  outbox!: Table<OutboxEntry, number>;
  meta!: Table<MetaEntry, string>;

  constructor() {
    super('odhaczka');
    this.version(1).stores({
      chains: 'id',
      stores: 'id, chainId',
      lists: 'id, status, storeId',
      listItems: 'id, listId, productId',
      products: 'id, normKey',
      productLinks: 'id, normKey, productId',
      visits: 'id, listId, storeId',
      orderEvents: 'id, storeId',
      outbox: '++seq',
      meta: 'key',
    });
    // Paragony z Lidl Plus (zapisuje je serwer) i przypięte artykuły pozycji.
    this.version(2).stores({
      articles: 'id, productId, code',
      receipts: 'id, listId, purchasedMs',
      receiptLines: 'id, receiptId, articleId, listItemId',
      listItems: 'id, listId, productId, articleId',
    });
    this.version(3).stores({ promotions: 'id, articleId, endMs' });
  }
}

export const db = new OdhaczkaDb();

export const lwwTable = (
  entity: keyof Pick<
    OdhaczkaDb,
    | 'chains'
    | 'stores'
    | 'lists'
    | 'listItems'
    | 'products'
    | 'productLinks'
    | 'visits'
    | 'articles'
    | 'receipts'
    | 'receiptLines'
    | 'promotions'
  >,
) =>
  db[entity] as unknown as Table<Record<string, unknown> & { id: string }, string>;

export async function getMeta<T>(key: string): Promise<T | undefined> {
  return (await db.meta.get(key))?.value as T | undefined;
}

export async function setMeta(key: string, value: unknown): Promise<void> {
  await db.meta.put({ key, value });
}

// Tożsamość urządzenia i zegar przeżywają wyczyszczenie danych: HLC nie może się cofnąć,
// a numery odhaczeń muszą rosnąć dalej (dziennik kolejności porządkuje po nich trasę).
const DEVICE_META = ['deviceId', 'hlc', 'seq'];

/**
 * Czyści dane lokalne (wylogowanie, inne konto na urządzeniu, opuszczenie domu).
 * keepMeta: dodatkowe klucze meta do zachowania (np. 'userId', gdy konto się nie zmienia).
 */
export async function wipeLocalData(keepMeta: readonly string[] = []): Promise<void> {
  await db.transaction('rw', db.tables, async () => {
    const keep = (await db.meta.bulkGet([...DEVICE_META, ...keepMeta])).filter((m): m is MetaEntry => !!m);
    for (const t of db.tables) await t.clear();
    await db.meta.bulkPut(keep);
  });
}
